import React, { useMemo } from 'react'
import { Tag } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { RefreshCw } from 'lucide-react'
import { podBulkUploadApi } from '@/api/services'
import type { BulkUploadHistoryBatchRow, BulkUploadHistoryJobRow, BulkUploadHistoryStats } from '@/types'
import { Button } from '@/components/ui/Button'
import { DashboardPanel } from '@/components/dashboard'
import { LeapDataTable } from '@/components/table/LeapDataTable'
import BulkUploadKpiGrid, { type BulkUploadKpiItem } from './BulkUploadKpiGrid'

type BulkUploadHistoryPanelProps = {
  onSelectLocalBatch?: (batchId: string) => void
  onSelectCloudJob?: (jobId: number) => void
}

function formatWhen(value: string | null | undefined): string {
  if (!value) return '—'
  const d = dayjs(value)
  return d.isValid() ? d.format('DD MMM YYYY, HH:mm') : '—'
}

function shortId(id: string): string {
  if (id.length <= 12) return id
  return `${id.slice(0, 8)}…${id.slice(-4)}`
}

function buildHistoryKpiItems(combined: BulkUploadHistoryStats['combined']): BulkUploadKpiItem[] {
  return [
    {
      key: 'totalFiles',
      labelKey: 'history.totalFiles',
      fallback: 'Total files (all time)',
      value: combined.totalFiles,
      tone: 'default',
    },
    {
      key: 'successful',
      labelKey: 'history.successful',
      fallback: 'Successful',
      value: combined.successful,
      tone: 'emerald',
    },
    {
      key: 'failed',
      labelKey: 'history.failed',
      fallback: 'Failed',
      value: combined.failed,
      tone: 'red',
    },
    {
      key: 'inProgress',
      labelKey: 'history.inProgress',
      fallback: 'In progress',
      value: combined.inProgress,
      tone: 'amber',
    },
    {
      key: 'attachments',
      labelKey: 'stats.attachments',
      fallback: 'Extra attachments',
      value: combined.attachmentsCreated,
      tone: 'purple',
    },
    {
      key: 'runs',
      labelKey: 'history.totalRuns',
      fallback: 'Upload runs',
      value: combined.totalRuns,
      tone: 'blue',
    },
  ]
}

const BulkUploadHistoryPanel: React.FC<BulkUploadHistoryPanelProps> = ({
  onSelectLocalBatch,
  onSelectCloudJob,
}) => {
  const { t } = useTranslation(['BulkPodUpload', 'Common'])

  const historyQuery = useQuery({
    queryKey: ['bulk-upload-history-stats'],
    queryFn: async () => {
      const res: any = await podBulkUploadApi.historyStats()
      return (res?.data?.data ?? res?.data) as BulkUploadHistoryStats | null
    },
    staleTime: 30_000,
  })

  const stats = historyQuery.data
  const combined = stats?.combined
  const kpiItems = useMemo(
    () => (combined ? buildHistoryKpiItems(combined) : []),
    [combined]
  )

  const localColumns = useMemo(
    () => [
      {
        title: t('BulkPodUpload:history.batchId', 'Batch'),
        dataIndex: 'batchId',
        key: 'batchId',
        render: (batchId: string) => (
          <button
            type="button"
            className="font-mono text-xs text-primary hover:underline"
            onClick={() => onSelectLocalBatch?.(batchId)}
            title={batchId}
          >
            {shortId(batchId)}
          </button>
        ),
      },
      {
        title: t('BulkPodUpload:form.docType', 'Document type'),
        dataIndex: 'documentType',
        key: 'documentType',
        width: 100,
      },
      {
        title: t('BulkPodUpload:history.files', 'Files'),
        dataIndex: 'fileCount',
        key: 'fileCount',
        width: 80,
      },
      {
        title: t('BulkPodUpload:history.outcomes', 'Outcomes'),
        key: 'outcomes',
        render: (_: unknown, row: BulkUploadHistoryBatchRow) => (
          <div className="flex flex-wrap gap-1">
            {row.completed > 0 ? (
              <Tag color="green">{row.completed} OK</Tag>
            ) : null}
            {row.skipped > 0 ? (
              <Tag color="gold">{row.skipped} skip</Tag>
            ) : null}
            {row.failed > 0 ? <Tag color="red">{row.failed} fail</Tag> : null}
            {row.pending + row.processing > 0 ? (
              <Tag color="blue">{row.pending + row.processing} open</Tag>
            ) : null}
            {row.rolledBack > 0 ? (
              <Tag>{row.rolledBack} rolled back</Tag>
            ) : null}
          </div>
        ),
      },
      {
        title: t('BulkPodUpload:history.lastActivity', 'Last activity'),
        dataIndex: 'lastUpdatedAt',
        key: 'lastUpdatedAt',
        width: 170,
        render: (v: string | null) => formatWhen(v),
      },
    ],
    [onSelectLocalBatch, t]
  )

  const cloudColumns = useMemo(
    () => [
      {
        title: t('BulkPodUpload:history.jobId', 'Job'),
        dataIndex: 'id',
        key: 'id',
        width: 90,
        render: (id: number) => (
          <button
            type="button"
            className="font-medium text-primary hover:underline"
            onClick={() => onSelectCloudJob?.(id)}
          >
            #{id}
          </button>
        ),
      },
      {
        title: t('BulkPodUpload:job.provider', 'Provider'),
        dataIndex: 'detectedProvider',
        key: 'detectedProvider',
        render: (v: string) => <span className="capitalize">{v?.replace('_', ' ') ?? '—'}</span>,
      },
      {
        title: t('BulkPodUpload:form.docType', 'Document type'),
        dataIndex: 'documentType',
        key: 'documentType',
        width: 100,
      },
      {
        title: t('BulkPodUpload:job.jobStatus', 'Status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (status: string) => <Tag className="capitalize">{status}</Tag>,
      },
      {
        title: t('BulkPodUpload:history.progress', 'Progress'),
        key: 'progress',
        render: (_: unknown, row: BulkUploadHistoryJobRow) => (
          <span className="tabular-nums text-sm">
            {row.processed}/{row.totalFiles} · {row.succeeded} OK · {row.failed} fail
          </span>
        ),
      },
      {
        title: t('BulkPodUpload:history.created', 'Created'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 170,
        render: (v: string | null) => formatWhen(v),
      },
    ],
    [onSelectCloudJob, t]
  )

  return (
    <DashboardPanel
      title={t('BulkPodUpload:history.title', 'All-time bulk upload history')}
      subtitle={
        combined?.lastActivityAt
          ? t('BulkPodUpload:history.subtitleWithDate', 'Lifetime counts across local folder and cloud imports · Last activity {{date}}', {
              date: formatWhen(combined.lastActivityAt),
            })
          : t(
              'BulkPodUpload:history.subtitle',
              'Lifetime counts across local folder uploads and cloud Drive/SharePoint imports'
            )
      }
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={historyQuery.isFetching}
          onClick={() => historyQuery.refetch()}
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${historyQuery.isFetching ? 'animate-spin' : ''}`} />
          {t('Common:general.refresh', 'Refresh')}
        </Button>
      }
    >
      {historyQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('Common:general.loading', 'Loading…')}</p>
      ) : historyQuery.isError ? (
        <p className="text-sm text-destructive">
          {t('BulkPodUpload:history.loadError', 'Could not load upload history')}
        </p>
      ) : stats ? (
        <div className="space-y-6">
          <BulkUploadKpiGrid items={kpiItems} columns={6} />

          <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <div>
              <div className="text-muted-foreground">
                {t('BulkPodUpload:history.localBatches', 'Local batches')}
              </div>
              <div className="mt-1 font-semibold tabular-nums">{stats.local.totalBatches}</div>
            </div>
            <div>
              <div className="text-muted-foreground">
                {t('BulkPodUpload:history.cloudJobs', 'Cloud jobs')}
              </div>
              <div className="mt-1 font-semibold tabular-nums">{stats.cloud.totalJobs}</div>
            </div>
            <div>
              <div className="text-muted-foreground">
                {t('BulkPodUpload:history.uniqueDocuments', 'Unique documents (local)')}
              </div>
              <div className="mt-1 font-semibold tabular-nums">{stats.local.uniqueDocuments}</div>
            </div>
            <div>
              <div className="text-muted-foreground">
                {t('BulkPodUpload:history.firstActivity', 'First upload')}
              </div>
              <div className="mt-1 font-medium">{formatWhen(combined?.firstActivityAt)}</div>
            </div>
          </div>

          {stats.local.recentBatches.length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">
                {t('BulkPodUpload:history.recentLocal', 'Recent local batches')}
              </h4>
              <LeapDataTable
                size="small"
                rowKey="batchId"
                columns={localColumns}
                dataSource={stats.local.recentBatches}
                pagination={{ pageSize: 5, hideOnSinglePage: true }}
              />
            </div>
          ) : null}

          {stats.cloud.recentJobs.length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">
                {t('BulkPodUpload:history.recentCloud', 'Recent cloud import jobs')}
              </h4>
              <LeapDataTable
                size="small"
                rowKey="id"
                columns={cloudColumns}
                dataSource={stats.cloud.recentJobs}
                pagination={{ pageSize: 5, hideOnSinglePage: true }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </DashboardPanel>
  )
}

export default BulkUploadHistoryPanel
